import { useState, useEffect, useCallback } from 'react'
import {
  fetchContentfulEntriesWithIncludes,
  resolveAssetUrl,
} from '../lib/contentful'

export interface Client {
  id: string
  name: string
  logo: string
}

// Contentful field shape for "clients" content type
interface ContentfulClientFields {
  clientName: string
  logo?: { sys: { id: string } }
  websiteUrl?: string
}

const STORAGE_KEY = 'belo_clients'
const CLIENTS_UPDATED_EVENT = 'belo_clients_updated'

function getStoredClients(): Client[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    return JSON.parse(raw)
  } catch {
    return []
  }
}

function saveClients(clients: Client[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(clients))
}

function dispatchClientsUpdate() {
  window.dispatchEvent(new Event(CLIENTS_UPDATED_EVENT))
}

export function addClient(data: { name: string; logo: string }): Client {
  const clients = getStoredClients()
  const newClient: Client = {
    id: `client_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    name: data.name,
    logo: data.logo,
  }
  clients.push(newClient)
  saveClients(clients)
  dispatchClientsUpdate()
  return newClient
}

export function removeClient(id: string): void {
  const clients = getStoredClients().filter((c) => c.id !== id)
  saveClients(clients)
  dispatchClientsUpdate()
}

function mapContentfulClient(entry: {
  sys: { id: string }
  fields: ContentfulClientFields
}): Client {
  return {
    id: entry.sys.id,
    name: entry.fields.clientName || '',
    logo: '',
  }
}

export function useClients() {
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const fetchFromCMS = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const { items, includes } =
        await fetchContentfulEntriesWithIncludes<ContentfulClientFields>(
          'clients',
        )

      const mapped: Client[] = items.map((entry) => {
        // Resolve the logo asset URL from includes
        const logoUrl = entry.fields.logo
          ? resolveAssetUrl(entry.fields.logo, includes)
          : ''

        return {
          id: entry.sys.id,
          name: entry.fields.clientName || '',
          logo: logoUrl,
        }
      })

      console.log(
        '[Clients] Mapped clients:',
        mapped.map((c) => ({ name: c.name, hasLogo: !!c.logo })),
      )

      setClients(mapped)
      saveClients(mapped)
    } catch (err) {
      console.error(
        'Failed to fetch clients from CMS, falling back to local:',
        err,
      )
      setError(true)
      setClients(getStoredClients())
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchFromCMS()
  }, [fetchFromCMS])

  useEffect(() => {
    const handler = () => setClients(getStoredClients())
    window.addEventListener(CLIENTS_UPDATED_EVENT, handler)
    return () => window.removeEventListener(CLIENTS_UPDATED_EVENT, handler)
  }, [])

  return { clients, loading, error, refetch: fetchFromCMS }
}

// Helper: convert a File to a base64 data URL
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}
